import * as tracking from "@app/tracking"
import { App as AppState, AllPopups } from "@app/types/root-state/app"
import { GenericAction } from "@app/types"
import { AppAction } from "./appCreator"
import { UiCoupon } from "@ng-mw/shared-react-components/src/modules/coupons/types/UiCoupon"

const init: AppState = {
    scrollPosition: {
        top: true,
        bottom: false,
        goingDown: false,
    },
    popupScrollPosition: {
        top: true,
        goingDown: false,
    },
    appUpgradePopup: {
        visible: false,
    },
    osUpgradePopup: {
        visible: false,
    },
    forcedUpgradePopup: {
        visible: false,
    },
    categoryFilterPopup: {
        visible: false,
    },
    bestBuyAllergensFilterPopup: {
        visible: false,
    },
    couponPopup: {
        visible: false,
    },
    couponInfoPopup: {
        visible: false,
    },
    productPopup: {
        visible: false,
    },
    couponDetailsPopup: {
        visible: false,
        derivedCoupon: undefined,
    },

    barcodePopup: {
        visible: false,
        productEan: "",
    },
    recipeRatingPopup: {
        visible: false,
        recipeItem: undefined,
    },
    recipePopup: {
        visible: false,
        recipeId: "",
    },
    trumfIdModal: {
        visible: false,
    },
    appOnboardingPoster: {
        visible: false,
    },
    loginPopup: {
        visible: false,
        title: "",
        text: "",
    },
    logOutPopup: {
        visible: false,
        title: "Den er grei!",
        text: "For å få tilgang til alle funksjonene i appen må du være logget inn som MENY MER-kunde. Hvis du ombestemmer deg senere kan du når som helst logge inn og registrere deg i kundeklubben.",
    },
    registrationPopup: {
        visible: false,
        data: undefined,
    },
    handoverPickerPopup: {
        visible: false,
    },
    checkoutPaymentPopup: {
        visible: false,
        paymentUrl: "",
    },
    insightAboutPopup: {
        visible: false,
    },
    feedbackPopup: {
        visible: false,
        data: undefined,
    },
    questbackFeedbackPopup: {
        visible: false,
        data: undefined,
    },
    feedbackSuccess: {
        visible: false,
    },
    insightKeyFiguresPopup: {
        visible: false,
        data: null,
    },
    climateNumbersPopup: {
        visible: false,
        data: null,
    },
    climateComparisonPopup: {
        visible: false,
        data: null,
    },
    climateInformationPopup: {
        visible: false,
    },
    climateProductGroupsInfoPopup: {
        visible: false,
    },
    dietEnergyPopup: {
        visible: false,
    },
    dietGroupPopup: {
        visible: false,
        tabIndex: 0,
    },
    dietPropertyPopup: {
        visible: false,
    },
    topBannerPopup: {
        visible: false,
    },
    featuredCampaignPopup: {
        visible: false,
    },
    dinnerSuggestionsPopup: {
        visible: false,
        startStep: undefined,
        mode: "onboarding",
    },
    dinnerSuggestionsPreferencesPopup: {
        visible: false,
        refreshSignal: 0,
    },
    // Transactions
    purchaseDetailsPopup: {
        visible: false,
    },
    purchaseComplaintPopup: {
        visible: false,
    },
    pushConsentsPopup: {
        visible: false,
    },
    qrPopup: {
        visible: false,
    },
    agreementDeactivationPopup: {
        visible: false,
    },
    removeShoppingListPopup: {
        visible: false,
        shoppingList: undefined,
    },
    changeNameShoppingListPopup: {
        visible: false,
    },
    createShoppingListPopup: {
        visible: false,
    },
    onboardingPoster: {
        visible: false,
    },
    newsPopup: {
        visible: false,
        data: {
            text: "",
            title: "",
            localStorageKey: "",
            cloudinaryId: "",
        },
    },
    activateLoyaltyClubPopup: {
        visible: false,
    },
    welcomePopup: {
        visible: false,
        data: null,
    },
    tmpPopup: {
        visible: false,
    },
    personalCampaignsPopup: {
        visible: false,
    },
    personalCampaignPopup: {
        visible: false,
        campaign: undefined,
    },
    gamePopup: {
        visible: false,
        data: {
            isJulelotteri: false,
            isKronelotteri: false,
        },
    },
    isFullscreenPopup: false,
    activePopups: [],
    lastBuild: process.env.LAST_BUILD,
    version: process.env.APP_VERSION,
    waitingForToken: true,
}

const hasFullscreenPopup = (activePopups: AppState["activePopups"]) =>
    activePopups.some(popup => popup.isFullscreenPopup)

const showPopup = (
    state: AppState,
    name: keyof AllPopups,
    moreState = {},
    isFullscreenPopup = false,
) => {
    tracking.modalOpened(name, moreState)
    const activePopups = [
        ...state.activePopups.filter(popup => popup.name !== name),
        { name, isFullscreenPopup },
    ]

    return {
        ...state,
        activePopups,
        isFullscreenPopup: isFullscreenPopup || hasFullscreenPopup(activePopups),
        [name]: {
            visible: true,
            ...moreState,
        },
    }
}
const hidePopup = (state: AppState, name: keyof AllPopups, moreState = {}) => {
    tracking.modalClosed(name, moreState)
    const activePopups = state.activePopups.filter(popup => popup.name !== name)

    return {
        ...state,
        activePopups,
        isFullscreenPopup: hasFullscreenPopup(activePopups),
        [name]: {
            ...state[name],
            visible: false,
            ...moreState,
        },
    }
}

export default (state = init, action: GenericAction) => {
    switch (action.type) {
        case AppAction.ScrollPositionChange: {
            const postion = action.payload

            return {
                ...state,
                scrollPosition: {
                    top: postion.top,
                    bottom: postion.bottom,
                    goingDown: postion.goingDown,
                },
            }
        }
        case AppAction.PopupScrollPositionChange: {
            const postion = action.payload

            return {
                ...state,
                popupScrollPosition: {
                    top: postion.top,
                    goingDown: postion.goingDown,
                },
            }
        }
        case AppAction.ShowCategoryFilterPopup:
            return showPopup(state, "categoryFilterPopup", { productViewId: action.payload }, true)
        case AppAction.HideCategoryFilterPopup:
            return hidePopup(state, "categoryFilterPopup")

        case AppAction.ShowBestBuyAllergensFilterPopup:
            return showPopup(state, "bestBuyAllergensFilterPopup", {}, true)
        case AppAction.HideBestBuyAllergensFilterPopup:
            return hidePopup(state, "bestBuyAllergensFilterPopup")
        case AppAction.ShowProductPopup:
            return showPopup(state, "productPopup", {}, true)
        case AppAction.HideProductPopup:
            return hidePopup(state, "productPopup")

        case AppAction.ShowCouponDetailsPopup: {
            const derivedCoupon: UiCoupon = action.payload
            return showPopup(state, "couponDetailsPopup", { derivedCoupon }, true)
        }
        case AppAction.HideCouponDetailsPopup:
            return hidePopup(state, "couponDetailsPopup")

        case AppAction.ShowBarcodePopup:
            return showPopup(state, "barcodePopup", {}, true)
        case AppAction.HideBarcodePopup:
            return hidePopup(state, "barcodePopup", { productEan: {} })
        case AppAction.BarcodeFound: {
            const productEan = action.payload
            return showPopup(state, "barcodePopup", { productEan })
        }

        case AppAction.ShowRecipePopup: {
            const recipeId = action.payload
            return showPopup(state, "recipePopup", { recipeId }, true)
        }
        case AppAction.HideRecipePopup:
            return hidePopup(state, "recipePopup")

        case AppAction.ShowRecipeRatingPopup: {
            const recipeItem = action.payload
            return showPopup(state, "recipeRatingPopup", { recipeItem })
        }
        case AppAction.HideRecipeRatingPopup:
            return hidePopup(state, "recipeRatingPopup")
        case AppAction.ShowTrumfIdModal:
            return showPopup(state, "trumfIdModal")
        case AppAction.HideTrumfIdModal:
            return hidePopup(state, "trumfIdModal")
        case AppAction.ShowAppOnboardingPoster:
            return showPopup(state, "appOnboardingPoster", {}, true)
        case AppAction.HideAppOnboardingPoster:
            return hidePopup(state, "appOnboardingPoster")
        case AppAction.ShowRegistrationPopup:
            return showPopup(state, "registrationPopup", { data: action.payload }, true)
        case AppAction.HideRegistrationPopup:
            return hidePopup(state, "registrationPopup")
        case AppAction.ShowCouponInfoPopup:
            return showPopup(state, "couponInfoPopup")
        case AppAction.HideCouponInfoPopup:
            return hidePopup(state, "couponInfoPopup")
        case AppAction.ShowCouponPopup:
            return showPopup(state, "couponPopup")
        case AppAction.HideCouponPopup:
            return hidePopup(state, "couponPopup")
        case AppAction.ShowHandoverPickerPopup:
            return showPopup(state, "handoverPickerPopup", {}, true)
        case AppAction.HideHandoverPickerPopup:
            return hidePopup(state, "handoverPickerPopup")
        case AppAction.ShowCheckoutPaymentPopup:
            return showPopup(state, "checkoutPaymentPopup", { paymentUrl: action.payload }, true)
        case AppAction.HideCheckoutPaymentPopup:
            return hidePopup(state, "checkoutPaymentPopup", { paymentUrl: {} })
        case AppAction.ShowInsightAboutPopup:
            return showPopup(state, "insightAboutPopup", {}, true)
        case AppAction.HideInsightAboutPopup:
            return hidePopup(state, "insightAboutPopup")
        case AppAction.ShowFeedbackPopup:
            return showPopup(state, "feedbackPopup", { data: action.payload }, true)
        case AppAction.HideFeedbackPopup:
            return hidePopup(state, "feedbackPopup", { data: {} })
        case AppAction.ShowQuestbackFeedbackPopup:
            return showPopup(state, "questbackFeedbackPopup", { data: action.payload }, true)
        case AppAction.HideQuestbackFeedbackPopup:
            return hidePopup(state, "questbackFeedbackPopup", { data: {} })
        case AppAction.ShowFeedbackSuccess:
            return {
                ...state,
                feedbackSuccess: {
                    id: action.payload,
                    visible: true,
                },
            }
        case AppAction.HideFeedbackSuccess:
            if (state.feedbackSuccess.id !== action.payload) {
                return state
            }

            return {
                ...state,
                feedbackSuccess: {
                    visible: false,
                },
            }
        case AppAction.ShowInsightKeyFiguresPopup:
            return showPopup(state, "insightKeyFiguresPopup", { data: action.payload }, true)
        case AppAction.HideInsightKeyFiguresPopup:
            return hidePopup(state, "insightKeyFiguresPopup")
        case AppAction.ShowClimateNumbersPopup:
            return showPopup(state, "climateNumbersPopup", {}, true)
        case AppAction.HideClimateNumbersPopup:
            return hidePopup(state, "climateNumbersPopup", { data: {} })
        case AppAction.ShowClimateComparisonPopup:
            return showPopup(state, "climateComparisonPopup", { data: action.payload }, true)
        case AppAction.HideClimateComparisonPopup:
            return hidePopup(state, "climateComparisonPopup", { data: {} })
        case AppAction.ShowClimateInformationPopup:
            return showPopup(state, "climateInformationPopup", {}, true)
        case AppAction.HideClimateInformationPopup:
            return hidePopup(state, "climateInformationPopup")
        case AppAction.ShowClimateProductGroupsInfoPopup:
            return showPopup(state, "climateProductGroupsInfoPopup", {}, true)
        case AppAction.HideClimateProductGroupsInfoPopup:
            return hidePopup(state, "climateProductGroupsInfoPopup")
        case AppAction.ShowDietEnergyPopup:
            return showPopup(state, "dietEnergyPopup", {}, true)
        case AppAction.HideDietEnergyPopup:
            return hidePopup(state, "dietEnergyPopup")
        case AppAction.ShowDietGroupPopup:
            return showPopup(state, "dietGroupPopup", {}, true)
        case AppAction.SetDietGroupPopupTabIndex:
            return showPopup(state, "dietGroupPopup", { tabIndex: action.payload }, true)
        case AppAction.HideDietGroupPopup:
            return hidePopup(state, "dietGroupPopup")
        case AppAction.ShowDietPropertyPopup:
            return showPopup(state, "dietPropertyPopup", {}, true)
        case AppAction.HideDietPropertyPopup:
            return hidePopup(state, "dietPropertyPopup")
        // case AppAction.ShowInsightOnboardingPopup:
        //     return showPopup(state, "insightOnboardingPopup", {}, true)
        // case AppAction.HideInsightOnboardingPopup:
        //     return hidePopup(state, "insightOnboardingPopup")
        case AppAction.ShowTopBannerPopup:
            return showPopup(state, "topBannerPopup")
        case AppAction.HideTopBannerPopup:
            return hidePopup(state, "topBannerPopup")
        case AppAction.ShowFeaturedCampaignPopup:
            return showPopup(state, "featuredCampaignPopup")
        case AppAction.HideFeaturedCampaignPopup:
            return hidePopup(state, "featuredCampaignPopup")
        case AppAction.ShowDinnerSuggestionsPopup:
            return showPopup(state, "dinnerSuggestionsPopup", action.payload, true)
        case AppAction.HideDinnerSuggestionsPopup:
            return hidePopup(state, "dinnerSuggestionsPopup", {
                startStep: undefined,
                mode: "onboarding",
            })
        case AppAction.ShowDinnerSuggestionsPreferencesPopup:
            return showPopup(
                state,
                "dinnerSuggestionsPreferencesPopup",
                { refreshSignal: state.dinnerSuggestionsPreferencesPopup.refreshSignal },
                true,
            )
        case AppAction.HideDinnerSuggestionsPreferencesPopup:
            return hidePopup(state, "dinnerSuggestionsPreferencesPopup")
        case AppAction.RefreshDinnerSuggestions:
            return {
                ...state,
                dinnerSuggestionsPreferencesPopup: {
                    ...state.dinnerSuggestionsPreferencesPopup,
                    refreshSignal: state.dinnerSuggestionsPreferencesPopup.refreshSignal + 1,
                },
            }

        // Transactions
        case AppAction.ShowPurchaseDetailsPopup:
            return showPopup(state, "purchaseDetailsPopup", {}, true)
        case AppAction.HidePurchaseDetailsPopup:
            return hidePopup(state, "purchaseDetailsPopup")
        case AppAction.ShowPurchaseComplaintPopup:
            return showPopup(state, "purchaseComplaintPopup")
        case AppAction.HidePurchaseComplaintPopup:
            return hidePopup(state, "purchaseComplaintPopup")

        case AppAction.ShowQrPopup:
            return showPopup(state, "qrPopup", {}, true)
        case AppAction.HideQrPopup:
            return hidePopup(state, "qrPopup")

        case AppAction.ShowAppUpgradePopup:
            return showPopup(state, "appUpgradePopup")
        case AppAction.HideAppUpgradePopup:
            return hidePopup(state, "appUpgradePopup")
        case AppAction.ShowOsUpgradePopup:
            return showPopup(state, "osUpgradePopup", {}, true)
        case AppAction.HideOsUpgradePopup:
            return hidePopup(state, "osUpgradePopup")
        case AppAction.ShowForcedUpgradePopup:
            return showPopup(state, "forcedUpgradePopup", {}, true)
        case AppAction.HideForcedUpgradePopup:
            return hidePopup(state, "forcedUpgradePopup")
        case AppAction.ShowNewsPopup: {
            const data = action.payload
            return showPopup(state, "newsPopup", { data })
        }
        case AppAction.HideNewsPopup:
            return hidePopup(state, "newsPopup")
        case AppAction.ShowActivateLoyaltyClubPopup:
            return showPopup(state, "activateLoyaltyClubPopup", {}, true)
        case AppAction.HideActivateLoyaltyClubPopup:
            return hidePopup(state, "activateLoyaltyClubPopup")
        case AppAction.ShowShoppingListRemovePopup:
            return showPopup(state, "removeShoppingListPopup", {
                shoppingList: action.payload,
            })
        case AppAction.HideShoppingListRemovePopup:
            return hidePopup(state, "removeShoppingListPopup")
        case AppAction.ShowShoppingListChangeNamePopup:
            return showPopup(state, "changeNameShoppingListPopup", {
                data: action.payload,
            })
        case AppAction.HideShoppingListChangeNamePopup:
            return hidePopup(state, "changeNameShoppingListPopup")
        case AppAction.ShowShoppingListCreatePopup:
            return showPopup(state, "createShoppingListPopup")
        case AppAction.HideShoppingListCreatePopup:
            return hidePopup(state, "createShoppingListPopup")
        case AppAction.ShowPushConsentsPopup:
            return showPopup(state, "pushConsentsPopup")
        case AppAction.HidePushConsentsPopup:
            return hidePopup(state, "pushConsentsPopup")
        case AppAction.ShowAgreementDeactivationPopup:
            return showPopup(state, "agreementDeactivationPopup")
        case AppAction.HideAgreementDeactivationPopup:
            return hidePopup(state, "agreementDeactivationPopup")
        case AppAction.ShowPersonalCampaignsPopup:
            return showPopup(state, "personalCampaignsPopup", {}, true)
        case AppAction.HidePersonalCampaignsPopup:
            return hidePopup(state, "personalCampaignsPopup")
        case AppAction.ShowPersonalCampaignPopup: {
            const campaign = action.payload
            return showPopup(state, "personalCampaignPopup", { campaign })
        }
        case AppAction.HidePersonalCampaignPopup:
            return hidePopup(state, "personalCampaignPopup")
        case AppAction.ShowLoginPopup: {
            const { title, text, externalLink, externalLinkText, imageUrl } =
                action.payload || state.loginPopup
            return showPopup(state, "loginPopup", {
                title,
                text,
                externalLink,
                externalLinkText,
                imageUrl,
            })
        }
        case AppAction.HideLoginPopup:
            return hidePopup(state, "loginPopup")
        case AppAction.ShowLogOutPopup: {
            const { title, text } = action.payload || state.logOutPopup
            return showPopup(state, "logOutPopup", { title, text })
        }
        case AppAction.HideLogOutPopup:
            return hidePopup(state, "logOutPopup")
        case AppAction.ShowWelcomePopup: {
            return showPopup(state, "welcomePopup", { data: action.payload })
        }
        case AppAction.HideWelcomePopup:
            return hidePopup(state, "welcomePopup")

        case AppAction.ShowTmpPopup:
            return showPopup(state, "tmpPopup")
        case AppAction.HideTmpPopup:
            return hidePopup(state, "tmpPopup")

        case AppAction.ShowGamePopup: {
            return showPopup(state, "gamePopup", { data: action.payload })
        }
        case AppAction.HideGamePopup:
            return hidePopup(state, "gamePopup")

        case AppAction.TokenLoaded:
            return {
                ...state,
                waitingForToken: false,
            }
        default:
            return state
    }
}
